// Projects Block Component
import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { FolderOpen, X, Search, Settings2, GripVertical } from 'lucide-react';
import projectsService from '../../services/projectsService';

const ProjectsBlock = ({ content, padding, backgroundColor, onUpdate }) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState(content?.selectedProjects || []);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('');
  const [filterTags, setFilterTags] = useState([]);
  const [editPadding, setEditPadding] = useState(padding || 'medium');
  const [editBackgroundColor, setEditBackgroundColor] = useState(backgroundColor || '');
  const [cardBackgroundColor, setCardBackgroundColor] = useState(content?.cardBackgroundColor || '#ffffff');
  const [headlineBackgroundColor, setHeadlineBackgroundColor] = useState(content?.headlineBackgroundColor || '#dbeafe');
  const [title, setTitle] = useState(content?.title || 'Project Showcase');
  const [titleFontFamily, setTitleFontFamily] = useState(content?.titleFontFamily || 'system');
  const [headlineFontFamily, setHeadlineFontFamily] = useState(content?.headlineFontFamily || 'system');
  const [bodyFontFamily, setBodyFontFamily] = useState(content?.bodyFontFamily || 'system');
  const [draggedIndex, setDraggedIndex] = useState(null);

  // State for fresh project data (for display)
  const [displayProjects, setDisplayProjects] = useState([]);
  const [loadingDisplay, setLoadingDisplay] = useState(false);

  // Get unique categories, difficulties, and tags from projects
  const [categories, setCategories] = useState([]);
  const [difficulties] = useState(['Beginner', 'Intermediate', 'Advanced']);
  const [allTags, setAllTags] = useState([]);

  // Helper to determine if a color is light or dark
  const isLightColor = (color) => {
    if (!color || color === 'transparent') return true;
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 186;
  };

  const getTextColorClass = (bgColor) => {
    return isLightColor(bgColor) ? 'text-gray-900' : 'text-white';
  };

  const getSubTextColorClass = (bgColor) => {
    return isLightColor(bgColor) ? 'text-gray-700' : 'text-gray-100';
  };

  const getFontFamily = (fontName) => {
    if (!fontName || fontName === 'system') {
      return 'ui-sans-serif, system-ui, sans-serif';
    }
    return `"${fontName}", sans-serif`;
  };

  // Font options
  const fonts = [
    { value: 'system', label: 'System Default' },
    { value: 'Coustard', label: 'Coustard' },
    { value: 'Alfa Slab One', label: 'Alfa Slab One' },
    { value: 'Ovo', label: 'Ovo' },
    { value: 'Fraunces', label: 'Fraunces' },
    { value: 'Lancelot', label: 'Lancelot' },
    { value: 'Arvo', label: 'Arvo' },
    { value: 'Josefin Slab', label: 'Josefin Slab' },
    { value: 'Outfit', label: 'Outfit' },
    { value: 'Cal Sans', label: 'Cal Sans' },
    { value: 'Tilt Warp', label: 'Tilt Warp' },
    { value: 'McLaren', label: 'McLaren' },
    { value: 'Mate', label: 'Mate' },
    { value: 'Buda', label: 'Buda' },
    { value: 'Bellefair', label: 'Bellefair' },
    { value: 'Questrial', label: 'Questrial' },
    { value: 'DM Mono', label: 'DM Mono' },
    { value: 'Quicksand', label: 'Quicksand' },
    { value: 'Nunito Sans', label: 'Nunito Sans' },
    { value: 'Roboto', label: 'Roboto' },
    { value: 'Manrope', label: 'Manrope' },
    { value: 'Jost', label: 'Jost' }
  ];

  // Memoize the project IDs string to prevent infinite loops
  const selectedProjectIds = useMemo(() => {
    return selectedProjects.map(p => p.projectId).sort().join(',');
  }, [selectedProjects]);

  useEffect(() => {
    if (showEditModal) {
      loadProjects();
    }
  }, [showEditModal]);

  // Load fresh project data for display
  useEffect(() => {
    const loadDisplayProjects = async () => {
      if (selectedProjects.length === 0) {
        setDisplayProjects([]);
        return;
      }

      try {
        setLoadingDisplay(true);
        const projectsData = await Promise.all(
          selectedProjects.map(async (selectedProject) => {
            try {
              const response = await projectsService.getProject(selectedProject.projectId);
              return {
                ...response,
                customHeadline: selectedProject.projectHeadline
              };
            } catch (err) {
              console.error(`Error fetching project ${selectedProject.projectId}:`, err);
              return null;
            }
          })
        );
        setDisplayProjects(projectsData.filter(p => p !== null));
      } catch (err) {
        console.error('Error loading display projects:', err);
      } finally {
        setLoadingDisplay(false);
      }
    };

    loadDisplayProjects();
  }, [selectedProjectIds]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await projectsService.getProjects();
      const projectsList = response.projects || [];
      setProjects(projectsList);

      // Extract unique categories and tags
      const uniqueCategories = [...new Set(projectsList.map(p => p.category).filter(Boolean))];
      setCategories(uniqueCategories);

      const uniqueTags = [...new Set(projectsList.flatMap(p => p.tags || []))];
      setAllTags(uniqueTags);
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    onUpdate({
      content: {
        selectedProjects,
        cardBackgroundColor,
        headlineBackgroundColor,
        title,
        titleFontFamily,
        headlineFontFamily,
        bodyFontFamily
      },
      padding: editPadding,
      backgroundColor: editBackgroundColor
    });
    setShowEditModal(false);
  };

  const handleCancel = () => {
    // Reset to original values
    setSelectedProjects(content?.selectedProjects || []);
    setCardBackgroundColor(content?.cardBackgroundColor || '#ffffff');
    setHeadlineBackgroundColor(content?.headlineBackgroundColor || '#dbeafe');
    setTitle(content?.title || 'Project Showcase');
    setTitleFontFamily(content?.titleFontFamily || 'system');
    setHeadlineFontFamily(content?.headlineFontFamily || 'system');
    setBodyFontFamily(content?.bodyFontFamily || 'system');
    setEditPadding(padding || 'medium');
    setEditBackgroundColor(backgroundColor || '');
    setShowEditModal(false);
  };

  const openEditModal = () => {
    setShowEditModal(true);
  };

  const handleProjectToggle = (projectId) => {
    const existing = selectedProjects.find(p => p.projectId === projectId);
    if (existing) {
      setSelectedProjects(selectedProjects.filter(p => p.projectId !== projectId));
    } else {
      setSelectedProjects([...selectedProjects, {
        projectId: projectId,
        projectHeadline: '',
        useCustomDescription: false,
        customDescription: ''
      }]);
    }
  };

  const handleHeadlineChange = (projectId, headline) => {
    setSelectedProjects(selectedProjects.map(p =>
      p.projectId === projectId ? { ...p, projectHeadline: headline } : p
    ));
  };

  const handleDescriptionToggle = (projectId) => {
    setSelectedProjects(selectedProjects.map(p =>
      p.projectId === projectId ? { ...p, useCustomDescription: !p.useCustomDescription } : p
    ));
  };

  const handleCustomDescriptionChange = (projectId, description) => {
    setSelectedProjects(selectedProjects.map(p =>
      p.projectId === projectId ? { ...p, customDescription: description } : p
    ));
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newSelectedProjects = [...selectedProjects];
    const draggedItem = newSelectedProjects[draggedIndex];
    newSelectedProjects.splice(draggedIndex, 1);
    newSelectedProjects.splice(index, 0, draggedItem);

    setSelectedProjects(newSelectedProjects);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const isProjectSelected = (projectId) => {
    return selectedProjects.some(p => p.projectId === projectId);
  };

  const getFilteredProjects = () => {
    return projects.filter(project => {
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchesName = project.name?.toLowerCase().includes(search);
        const matchesDescription = project.description?.toLowerCase().includes(search);
        if (!matchesName && !matchesDescription) return false;
      }

      // Category filter
      if (filterCategory && project.category !== filterCategory) return false;

      // Difficulty filter
      if (filterDifficulty && project.difficulty !== filterDifficulty) return false;

      // Tags filter
      if (filterTags.length > 0) {
        const projectTags = project.tags || [];
        const hasAllTags = filterTags.every(tag => projectTags.includes(tag));
        if (!hasAllTags) return false;
      }

      return true;
    });
  };

  const handleTagFilterToggle = (tag) => {
    if (filterTags.includes(tag)) {
      setFilterTags(filterTags.filter(t => t !== tag));
    } else {
      setFilterTags([...filterTags, tag]);
    }
  };

  // Rich text editor configuration for headlines
  const headlineModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ],
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterCategory('');
    setFilterDifficulty('');
    setFilterTags([]);
  };

  const getPaddingClass = () => {
    switch (padding) {
      case 'none': return 'p-0';
      case 'small': return 'p-2';
      case 'large': return 'p-6';
      default: return 'p-4';
    }
  };

  const renderEditModal = () => {
    const filteredProjects = getFilteredProjects();
    const hasActiveFilters = searchTerm || filterCategory || filterDifficulty || filterTags.length > 0;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Modal Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100">
              Configure Projects Block
            </h3>
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-[#7A99D8] text-white rounded hover:bg-[#6A89C8]"
              >
                Save ({selectedProjects.length} selected)
              </button>
              <button
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded text-[#3C3C3C] dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>

          {/* Modal Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {/* Two Column Layout */}
            <div className="grid grid-cols-2 gap-6 mb-6">
              {/* LEFT COLUMN - Available Projects */}
              <div>
                <h4 className="font-medium text-[#3C3C3C] dark:text-gray-100 mb-3">
                  Available Projects ({projects.length})
                </h4>
                {loading ? (
                  <div className="text-center py-8 text-gray-500">Loading projects...</div>
                ) : projects.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No projects available</div>
                ) : (
                  <div className="space-y-2 max-h-[500px] overflow-y-auto">
                    {projects.map(project => {
                      const selected = isProjectSelected(project.id);
                      return (
                        <div
                          key={project.id}
                          className={`flex items-start gap-3 p-3 border rounded cursor-pointer transition-colors ${
                            selected
                              ? 'border-[#7A99D8] bg-blue-50 dark:bg-blue-900/20'
                              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                          }`}
                          onClick={() => handleProjectToggle(project.id)}
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => {}}
                            className="mt-1"
                          />
                          {project.main_image_url && (
                            <img
                              src={project.main_image_url}
                              alt={project.name}
                              className="w-16 h-16 object-cover rounded"
                            />
                          )}
                          <div className="flex-1">
                            <h5 className="font-medium text-[#3C3C3C] dark:text-gray-100">
                              {project.name}
                            </h5>
                            {project.description && (
                              <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mt-1">
                                {project.description}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* RIGHT COLUMN - Block Title & Selected Projects */}
              <div className="space-y-6">
                {/* Block Title */}
                <div>
                  <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                    Block Title
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Project Showcase"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
                  />
                </div>

                {/* Selected Projects with Headlines */}
                {selectedProjects.length > 0 && (
                  <div>
                    <h4 className="font-medium text-[#3C3C3C] dark:text-gray-100 mb-3">
                      Selected Projects - Add Headlines
                    </h4>
                    <div className="space-y-4 max-h-[420px] overflow-y-auto">
                      {selectedProjects.map((selectedProject, index) => {
                        const project = projects.find(p => p.id === selectedProject.projectId);
                        if (!project) return null;

                        return (
                          <div
                            key={selectedProject.projectId}
                            draggable
                            onDragStart={() => handleDragStart(index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragEnd={handleDragEnd}
                            className={`border border-gray-200 dark:border-gray-700 rounded p-4 bg-white dark:bg-gray-800 cursor-move transition-opacity ${
                              draggedIndex === index ? 'opacity-50' : 'opacity-100'
                            }`}
                          >
                            <div className="flex gap-3 mb-3">
                              <div className="flex items-center cursor-grab active:cursor-grabbing">
                                <GripVertical className="w-5 h-5 text-gray-400" />
                              </div>
                              {project.main_image_url && (
                                <img
                                  src={project.main_image_url}
                                  alt={project.name}
                                  className="w-16 h-16 object-cover rounded flex-shrink-0"
                                />
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <h5 className="font-medium text-[#3C3C3C] dark:text-gray-100 truncate">
                                    {project.name}
                                  </h5>
                                  <button
                                    onClick={() => handleProjectToggle(selectedProject.projectId)}
                                    className="text-red-500 hover:text-red-600 flex-shrink-0"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                            <div className="space-y-3">
                              <div>
                                <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                                  Project Headline (optional)
                                </label>
                                <ReactQuill
                                  theme="snow"
                                  value={selectedProject.projectHeadline || ''}
                                  onChange={(value) => handleHeadlineChange(selectedProject.projectId, value)}
                                  modules={headlineModules}
                                  placeholder="Add a custom headline for this project..."
                                  className="bg-white dark:bg-gray-700 rounded"
                                />
                              </div>

                              {/* Custom Description Toggle */}
                              <div>
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={selectedProject.useCustomDescription || false}
                                    onChange={() => handleDescriptionToggle(selectedProject.projectId)}
                                    className="w-4 h-4 text-[#7A99D8] border-gray-300 dark:border-gray-600 rounded focus:ring-[#7A99D8] focus:ring-2"
                                  />
                                  <span className="text-sm font-medium text-[#3C3C3C] dark:text-gray-100">
                                    Use custom description
                                  </span>
                                </label>
                                <p className="text-xs text-gray-500 mt-1 ml-6">
                                  Override the project description with custom text
                                </p>
                              </div>

                              {/* Custom Description Field */}
                              {selectedProject.useCustomDescription && (
                                <div>
                                  <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                                    Custom Description
                                  </label>
                                  <textarea
                                    value={selectedProject.customDescription || ''}
                                    onChange={(e) => handleCustomDescriptionChange(selectedProject.projectId, e.target.value)}
                                    rows={4}
                                    placeholder="Enter custom description..."
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100 resize-vertical"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* BOTTOM - Font, Color and Padding Settings */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6 space-y-4">
              <h4 className="font-medium text-[#3C3C3C] dark:text-gray-100">Block Styling</h4>

              {/* Font Settings */}
              <div>
                <h5 className="text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-3">Typography</h5>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
                      Title Font
                    </label>
                    <select
                      value={titleFontFamily}
                      onChange={(e) => setTitleFontFamily(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
                      style={{ fontFamily: getFontFamily(titleFontFamily) }}
                    >
                      {fonts.map(font => (
                        <option key={font.value} value={font.value} style={{ fontFamily: getFontFamily(font.value) }}>
                          {font.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
                      Headline Font
                    </label>
                    <select
                      value={headlineFontFamily}
                      onChange={(e) => setHeadlineFontFamily(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
                      style={{ fontFamily: getFontFamily(headlineFontFamily) }}
                    >
                      {fonts.map(font => (
                        <option key={font.value} value={font.value} style={{ fontFamily: getFontFamily(font.value) }}>
                          {font.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
                      Body Font
                    </label>
                    <select
                      value={bodyFontFamily}
                      onChange={(e) => setBodyFontFamily(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
                      style={{ fontFamily: getFontFamily(bodyFontFamily) }}
                    >
                      {fonts.map(font => (
                        <option key={font.value} value={font.value} style={{ fontFamily: getFontFamily(font.value) }}>
                          {font.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Color and Padding Settings */}
              <div>
                <h5 className="text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-3">Colors & Spacing</h5>
                <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                    Padding
                  </label>
                  <select
                    value={editPadding}
                    onChange={(e) => setEditPadding(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
                  >
                    <option value="none">None</option>
                    <option value="small">Small</option>
                    <option value="medium">Medium</option>
                    <option value="large">Large</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                    Background Color
                  </label>
                  <input
                    type="color"
                    value={editBackgroundColor || '#ffffff'}
                    onChange={(e) => setEditBackgroundColor(e.target.value)}
                    className="w-full h-10 px-1 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                    Card Background
                  </label>
                  <input
                    type="color"
                    value={cardBackgroundColor || '#ffffff'}
                    onChange={(e) => setCardBackgroundColor(e.target.value)}
                    className="w-full h-10 px-1 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                    Headline Background
                  </label>
                  <input
                    type="color"
                    value={headlineBackgroundColor || '#dbeafe'}
                    onChange={(e) => setHeadlineBackgroundColor(e.target.value)}
                    className="w-full h-10 px-1 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                  />
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Preview mode
  return (
    <>
      <div className={getPaddingClass()} style={{ backgroundColor }}>
        {selectedProjects.length === 0 ? (
          <div className="text-center">
            <FolderOpen className="w-12 h-12 text-[#7A99D8] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100 mb-2">
              Projects
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Click edit to configure projects
            </p>
          </div>
        ) : loadingDisplay ? (
          <div className="text-center py-8 text-gray-500">Loading projects...</div>
        ) : (
          <>
            {/* Title */}
            {content?.title && (
              <h3
                className={`text-xl font-semibold mb-4 ${getTextColorClass(backgroundColor)}`}
                style={{ fontFamily: getFontFamily(content?.titleFontFamily) }}
              >
                {content.title}
              </h3>
            )}

            <div className="space-y-6">
              {displayProjects.map((project, index) => {
                const cardBg = content?.cardBackgroundColor || '#ffffff';
                const selectedProject = selectedProjects.find(sp => sp.projectId === project.id);
                const displayDescription = selectedProject?.useCustomDescription
                  ? selectedProject?.customDescription
                  : project.description;

                return (
                <div
                  key={project.id}
                  className="rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
                  style={{ backgroundColor: cardBg }}
                >
                <div className="p-4">
                  {/* Project Image - floated left on desktop */}
                  {project.main_image_url && (
                    <img
                      src={project.main_image_url}
                      alt={project.name}
                      className="w-full h-48 md:w-[300px] md:h-[300px] object-cover md:float-left md:mr-4 md:mb-4 mb-4 rounded"
                    />
                  )}

                  {/* Custom Headline - NOW FIRST */}
                  {project.customHeadline && (
                    <div
                      className="mb-3 p-3 rounded-lg border-l-4 border-[#7A99D8]"
                      style={{
                        backgroundColor: content?.headlineBackgroundColor || '#dbeafe',
                        fontFamily: getFontFamily(content?.headlineFontFamily)
                      }}
                    >
                      <div
                        className={`text-sm leading-relaxed ${getTextColorClass(content?.headlineBackgroundColor || '#dbeafe')}`}
                        dangerouslySetInnerHTML={{ __html: project.customHeadline }}
                      />
                    </div>
                  )}

                  {/* Project Name - NOW SECOND (removed number badge) */}
                  <h4
                    className={`text-lg font-bold mb-2 ${getTextColorClass(cardBg)}`}
                    style={{ fontFamily: getFontFamily(content?.bodyFontFamily) }}
                  >
                    {project.name}
                  </h4>

                  {/* Project Description (or custom description) */}
                  {displayDescription && (
                    <div
                      className={`text-sm leading-relaxed ${getSubTextColorClass(cardBg)} whitespace-pre-wrap`}
                      style={{ fontFamily: getFontFamily(content?.bodyFontFamily) }}
                    >
                      {displayDescription}
                    </div>
                  )}

                  {/* Clear float */}
                  <div className="clear-both"></div>
                </div>
              </div>
                );
              })}
            </div>
          </>
        )}

        {/* Hidden edit trigger for PageBuilder */}
        <button
          data-edit-trigger
          onClick={openEditModal}
          className="hidden"
          aria-label="Edit block"
        />
      </div>

      {showEditModal && createPortal(renderEditModal(), document.body)}
    </>
  );
};

export default ProjectsBlock;
